package co.maytes.standupbot.dashboard.features.analytics

/**
 * Categorical palette for charts. The first three slots are tuned for the
 * Haiku / Sonnet / Opus stack in [CostChart] — Linear indigo is slot 0
 * (Haiku is cheapest + most common), a violet shift is slot 1, and a deep
 * plum for slot 2 so the stack reads ordered without additional labels.
 *
 * Slots 3+ are added for multi-standup velocity. Accent-dependent colors use
 * CSS variables when they need to flip with theme; category colors are
 * explicit rgb() so the charts look identical in light and dark.
 */
object ChartPalette {
    /** Kept deliberately small — charts should not require a reader to learn a rainbow. */
    val categorical: List<String> = listOf(
        "rgb(94, 106, 210)",   // Linear indigo — brand accent
        "rgb(156, 122, 216)",  // Violet
        "rgb(213, 109, 181)",  // Plum
        "rgb(56, 178, 172)",   // Teal
        "rgb(244, 162, 97)",   // Warm sand
        "rgb(99, 115, 129)",   // Slate
        "rgb(5, 150, 105)",    // Success green (matches desaturated semantic success)
    )

    fun at(index: Int): String = categorical[index.mod(categorical.size)]

    /** Named model colors for CostChart's legend. */
    val modelColors: Map<String, String> = mapOf(
        "Haiku" to categorical[0],
        "Sonnet" to categorical[1],
        "Opus" to categorical[2],
    )
}

/** Cents → "$1.23" (or "$1,234.56" with grouping). */
fun formatCurrency(cents: Long, currency: String = "USD"): String {
    if (currency != "USD") return "$currency ${formatDollars(cents)}"
    return "$" + formatDollars(cents)
}

private fun formatDollars(cents: Long): String {
    val negative = cents < 0
    val absCents = if (negative) -cents else cents
    val dollars = absCents / 100
    val remainder = (absCents % 100).toInt()
    val dollarsStr = groupThousands(dollars)
    val cs = remainder.toString().padStart(2, '0')
    return (if (negative) "-" else "") + "$dollarsStr.$cs"
}

private fun groupThousands(n: Long): String {
    val s = n.toString()
    if (s.length <= 3) return s
    val out = StringBuilder()
    val first = s.length % 3
    if (first != 0) {
        out.append(s.substring(0, first))
        if (s.length > first) out.append(',')
    }
    var i = first
    while (i < s.length) {
        out.append(s.substring(i, i + 3))
        if (i + 3 < s.length) out.append(',')
        i += 3
    }
    return out.toString()
}

/** "2026-04" → "Apr 2026". Falls back to the raw input on a parse miss. */
fun formatMonthLabel(yyyyMm: String): String {
    val parts = yyyyMm.split('-')
    if (parts.size < 2) return yyyyMm
    val year = parts[0].toIntOrNull() ?: return yyyyMm
    val month = parts[1].toIntOrNull() ?: return yyyyMm
    val name = when (month) {
        1 -> "Jan"; 2 -> "Feb"; 3 -> "Mar"; 4 -> "Apr"
        5 -> "May"; 6 -> "Jun"; 7 -> "Jul"; 8 -> "Aug"
        9 -> "Sep"; 10 -> "Oct"; 11 -> "Nov"; 12 -> "Dec"
        else -> return yyyyMm
    }
    return "$name $year"
}
